/**
 * Server settings (spec §8.26, M26): for administrators — the runtime settings, user accounts and the
 * history of changes. Anyone else gets one line; the server refuses them too (§10.8).
 */
import { clsx } from "clsx";
import { useState } from "react";
import { useMe } from "../api/hooks";
import { useT } from "../i18n";
import { HistoryTab } from "./HistoryTab";
import { SettingsTab } from "./SettingsTab";
import { UsersTab } from "./UsersTab";
import { TAB } from "./labels";

const TABS = ["settings", "users", "history"] as const;
type Tab = (typeof TABS)[number];

export function ServerSettingsPage() {
  const t = useT();
  const me = useMe();
  const [tab, setTab] = useState<Tab>("settings");
  if (!me.data) return null;
  if (!me.data.isAdmin)
    return (
      <p className="mx-auto max-w-3xl px-9 pt-8 text-sm text-neutral-500" data-testid="server-admins-only">
        {t("server.adminsOnly")}
      </p>
    );
  return (
    <div className="mx-auto w-full max-w-4xl px-9 pb-16 pt-8" data-testid="server-settings">
      <h1 className="mb-4 text-[26px] font-semibold">{t("server.title")}</h1>
      <div className="mb-5 flex gap-1 border-b border-neutral-200 dark:border-neutral-800" role="tablist">
        {TABS.map((id) => (
          <button
            key={id}
            role="tab"
            aria-selected={tab === id}
            className={clsx(
              "-mb-px border-b-2 px-3 py-2 text-sm transition",
              tab === id ? "border-accent font-medium text-neutral-900 dark:text-neutral-100" : "border-transparent text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100",
            )}
            onClick={() => setTab(id)}
            data-testid={`server-tab-${id}`}
          >
            {t(TAB[id] ?? id)}
          </button>
        ))}
      </div>
      {tab === "settings" && <SettingsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "history" && <HistoryTab />}
    </div>
  );
}
